import React, { useState, useEffect } from 'react';
import { Blurhash } from 'react-blurhash';

interface BlurhashImageProps {
  src: string;
  alt?: string;
  blurhash?: string;
  width?: number | string;
  height?: number | string;
  style?: React.CSSProperties;
  className?: string;
}

const BlurhashImage: React.FC<BlurhashImageProps> = ({
  src,
  alt = '',
  blurhash,
  width = '100%',
  height = '100%',
  style = {},
  className = '',
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setImageLoaded(false);
    setIsError(false);
    
    // Reset loading state when src changes
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setIsError(true);
    img.src = src;
    
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  // Compute a valid fallback blurhash if none provided or invalid
  const validBlurhash = blurhash && blurhash.length >= 6 ? blurhash : 'LEHV6nWB2yk8pyo0adR*.7kCMdnj';

  return (
    <div 
      style={{ 
        position: 'relative', 
        width: width, 
        height: height, 
        ...style 
      }}
      className={className}
    >
      {/* Blurhash placeholder */}
      {!imageLoaded && blurhash && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden'
          }}
        >
          <Blurhash
            hash={validBlurhash}
            width="100%"
            height="100%"
            resolutionX={32}
            resolutionY={32}
            punch={1}
          />
          
          {/* Loading indicator */}
          <div 
            style={{ 
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '30px', 
              height: '30px', 
              border: '2px solid rgba(255,255,255,0.2)',
              borderRadius: '50%',
              borderTopColor: 'rgba(255,255,255,0.7)',
              animation: 'spin 1s linear infinite'
            }} 
          />
        </div>
      )}
      
      {/* Basic placeholder if no blurhash */}
      {!imageLoaded && !blurhash && (
        <div 
          style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0,
            backgroundColor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}
        >
          <div 
            style={{ 
              width: '30px', 
              height: '30px', 
              border: '2px solid rgba(0,0,0,0.1)',
              borderRadius: '50%',
              borderTopColor: 'rgba(0,0,0,0.3)',
              animation: 'spin 1s linear infinite'
            }} 
          />
        </div>
      )}
      
      {/* Actual image */}
      <img
        src={src}
        alt={alt}
        style={{
          display: imageLoaded ? 'block' : 'none',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
        loading="lazy"
      />
      
      {/* Error fallback */}
      {isError && (
        <div 
          style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            color: '#666'
          }}
        >
          Bild konnte nicht geladen werden
        </div>
      )}
      
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default BlurhashImage; 